<script setup lang="ts">
import { X as XIcon } from '@lucide/vue'

export interface AnimatedXButtonProps {
  size?: 'sm' | 'md' | 'lg'
  ariaLabel?: string
  disabled?: boolean
  textColorClass?: string
  hoverTextColorClass?: string
}

const props = withDefaults(defineProps<AnimatedXButtonProps>(), {
  size: 'md',
  ariaLabel: 'Close',
  disabled: false,
  textColorClass: 'text-gray-400',
  hoverTextColorClass: 'hover:text-gray-600',
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const handleClick = (event: MouseEvent) => {
  if (!props.disabled) {
    emit('click', event)
  }
}

// Size-based classes
const sizeClasses = {
  sm: {
    button: 'p-1',
    icon: 'w-4 h-4',
  },
  md: {
    button: 'p-2',
    icon: 'w-5 h-5',
  },
  lg: {
    button: 'p-3',
    icon: 'w-6 h-6',
  },
}
</script>

<template>
  <button
    type="button"
    :disabled="disabled"
    :aria-label="ariaLabel"
    :class="[
      // Base styling
      props.textColorClass,
      'rounded-full',
      'hover:cursor-pointer',
      // Size-based spacing
      sizeClasses[size].button,
      // Interactive states (only when not disabled)
      !disabled && [
        props.hoverTextColorClass,
        'hover:bg-gray-100',
        'cursor-pointer',
        'hover:scale-110',
        'hover:rotate-90',
        'transition-all',
        'duration-200',
        'ease-out',
        'active:scale-95',
      ],
      // Focus states
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-primary-500',
      'focus:ring-offset-2',
      // Disabled states
      disabled && ['opacity-50', 'cursor-not-allowed'],
    ]"
    @click="handleClick"
  >
    <XIcon :class="[sizeClasses[size].icon, 'transition-transform', 'duration-200', 'ease-out']" />
  </button>
</template>
